import React from 'react';
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  getDay,
  isBefore,
  isSameDay,
} from 'date-fns';
import { useLang } from '../LangContext';

interface TimeSlot {
  start: string;
  end: string;
}

interface AvailabilityEntry {
  date: string;
  hourly_slots?: TimeSlot[];
  free_slots?: TimeSlot[];
  available_ranges?: { start: string; end: string }[];
  message?: string;
}

interface Props {
  currentMonthDate: Date;
  availability: AvailabilityEntry[];
  selectedDay: Date | null;
  onSelect: (day: Date) => void;
}

const MonthCalendar = ({ currentMonthDate, availability, selectedDay, onSelect }: Props) => {
  const { t } = useLang();
  const start = startOfMonth(currentMonthDate);
  const end = endOfMonth(currentMonthDate);
  const days = eachDayOfInterval({ start, end });
  const firstDayOffset = getDay(start) === 0 ? 6 : getDay(start) - 1;
  const paddedDays: (Date | null)[] = Array(firstDayOffset).fill(null).concat(days);
  const today = new Date();

  const findEntry = (day: Date) =>
    availability.find((entry) => entry.date === format(day, 'yyyy-MM-dd'));

  return (
    <div>
      <div className="calendar-header" role="row">
        {t.weekdays.map(({ abbr, full }) => (
          <div key={abbr} className="weekday-label" role="columnheader" aria-label={full}>{abbr}</div>
        ))}
      </div>
      <div className="calendar" role="grid" aria-label="Select appointment date">
        {paddedDays.map((day, index) => {
          if (!day) return <div key={index} className="calendar-day empty" aria-hidden="true" />;

          const entry = findEntry(day);
          const hasSlots =
            entry &&
            ((entry.hourly_slots && entry.hourly_slots.length > 0) ||
              (entry.free_slots && entry.free_slots.length > 0)) &&
            !entry.message;
          const isPastOrToday = isBefore(day, today) || isSameDay(day, today);
          const clickable = hasSlots && !isPastOrToday;
          const isSelected = isSameDay(day, selectedDay ?? new Date(0));

          return (
            <button
              key={format(day, 'yyyy-MM-dd')}
              className={`calendar-day ${clickable ? 'clickable' : 'disabled'} ${isSelected ? 'selected' : ''}`}
              onClick={() => clickable && onSelect(day)}
              disabled={!clickable}
              aria-label={format(day, 'dd.MM.yy')}
              aria-selected={isSelected}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
